import type { ClientMsg, PackedMsg, ServerMsg } from '@home/shared';

/** Mirrors protocol.ts; duplicated so the bundle doesn't pull zod in at runtime. */
export const CLOSE_UNAUTHENTICATED = 4401;
export const CLOSE_REVOKED = 4403;

export type SocketStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'unauthenticated' | 'revoked';

export interface SocketHandlers {
  onMessage: (msg: ServerMsg | PackedMsg) => void;
  onStatus: (status: SocketStatus) => void;
  /** Fired on every (re)open so the store can resync and replay nothing stale. */
  onOpen: () => void;
}

/**
 * One websocket for every home. Reconnects with capped exponential backoff and
 * jitter; reconnects immediately when the page becomes visible or the network
 * comes back. Terminal closes (4401/4403) stop the loop.
 */
export class HomeSocket {
  private ws: WebSocket | null = null;
  private retry = 0;
  private reconnectTimer: number | undefined;
  private pingTimer: number | undefined;
  private lastPong = 0;
  private stopped = true;
  private status: SocketStatus = 'idle';

  constructor(private readonly h: SocketHandlers) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.retry = 0;
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('online', this.onOnline);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.onOnline);
    window.clearTimeout(this.reconnectTimer);
    window.clearInterval(this.pingTimer);
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.close();
    }
    this.setStatus('idle');
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(msg: ClientMsg): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  private setStatus(s: SocketStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.h.onStatus(s);
  }

  private connect = (): void => {
    if (this.stopped) return;
    window.clearTimeout(this.reconnectTimer);
    this.setStatus(this.retry === 0 && this.status !== 'reconnecting' ? 'connecting' : 'reconnecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    // z=1: compact wire encoding (shared/wire.ts), unpacked in the store.
    const ws = new WebSocket(`${proto}://${location.host}/ws?z=1`);
    this.ws = ws;
    // A handshake that never completes (captive portal, stalled proxy) would
    // otherwise sit in CONNECTING forever.
    const openTimer = window.setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) ws.close();
    }, 8000);

    ws.onopen = () => {
      window.clearTimeout(openTimer);
      this.retry = 0;
      this.lastPong = Date.now();
      this.setStatus('live');
      this.h.onOpen();
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        // A half-open socket (phone switched networks) never closes on its own.
        if (Date.now() - this.lastPong > 70_000) {
          ws.close();
          return;
        }
        ws.send(JSON.stringify({ t: 'ping' }));
      }, 25_000);
    };

    ws.onmessage = (ev: MessageEvent<string>) => {
      let msg: ServerMsg | PackedMsg;
      try {
        msg = JSON.parse(ev.data) as ServerMsg | PackedMsg;
      } catch {
        return;
      }
      // Any traffic proves the socket is alive, not just pongs.
      this.lastPong = Date.now();
      this.h.onMessage(msg);
    };

    ws.onclose = (ev) => {
      window.clearTimeout(openTimer);
      window.clearInterval(this.pingTimer);
      if (this.ws === ws) this.ws = null;
      if (this.stopped) return;
      if (ev.code === CLOSE_UNAUTHENTICATED) {
        this.stopped = true;
        this.setStatus('unauthenticated');
        return;
      }
      if (ev.code === CLOSE_REVOKED) {
        this.stopped = true;
        this.setStatus('revoked');
        return;
      }
      this.setStatus('reconnecting');
      const base = Math.min(500 * 2 ** this.retry++, 15_000);
      const delay = base / 2 + Math.random() * (base / 2);
      this.reconnectTimer = window.setTimeout(this.connect, delay);
    };

    ws.onerror = () => ws.close();
  };

  private onVisible = (): void => {
    if (document.visibilityState !== 'visible' || this.stopped) return;
    if (this.isOpen) {
      // States may have moved on while the page was frozen in the background.
      this.send({ t: 'resync' });
    } else {
      this.retry = 0;
      this.connect();
    }
  };

  private onOnline = (): void => {
    if (!this.stopped && !this.isOpen) {
      this.retry = 0;
      this.connect();
    }
  };
}
